import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, Star, Heart, ShieldAlert } from 'lucide-react';

const buildDefaultQuantities = (options, minOrderAmount, initialQuantities) => {
  if (initialQuantities) {
    return { ...initialQuantities };
  }
  const quantities = {};
  options.forEach((option, index) => {
    quantities[option.id] = index === 0 ? minOrderAmount || 1 : 0;
  });
  return quantities;
};

export const ItemModal = ({
  item,
  restaurant,
  isOpen,
  onClose,
  onAddToCart,
  initialQuantities,
  initialInstructions,
  initialRecipientName,
}) => {
  const options = item?.options?.length > 0 ? item.options : [];
  const [quantities, setQuantities] = useState(() =>
    buildDefaultQuantities(options, item?.minOrderAmount, initialQuantities),
  );
  const [instructions, setInstructions] = useState(initialInstructions || '');
  const [recipientName, setRecipientName] = useState(
    initialRecipientName || '',
  );
  const [showRecipientInput, setShowRecipientInput] = useState(
    !!initialRecipientName,
  );
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setQuantities(
      buildDefaultQuantities(options, item?.minOrderAmount, initialQuantities),
    );
    setInstructions(initialInstructions || '');
    setRecipientName(initialRecipientName || '');
    setShowRecipientInput(!!initialRecipientName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, item?.id]);

  if (!isOpen || !item) return null;

  const totalPortions = Object.values(quantities).reduce(
    (sum, q) => sum + (parseInt(q, 10) || 0),
    0,
  );
  const totalPrice = options.reduce(
    (sum, option) => sum + (parseInt(quantities[option.id], 10) || 0) * option.price,
    0,
  );

  const setOptionQuantity = (optionId, quantity) => {
    setQuantities(prev => ({
      ...prev,
      [optionId]: Math.max(0, quantity),
    }));
  };

  const handleSave = () => {
    if (totalPortions <= 0) return;
    onAddToCart(item, quantities, instructions, recipientName);
    onClose();
  };

  return React.createElement(
    'div',
    {
      className:
        'fixed inset-0 z-50 overflow-y-auto bg-[#202124]/70 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4',
      onClick: onClose,
    },
    React.createElement(
      'div',
      {
        className:
          'relative bg-white rounded-2xl md:rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden border border-panda-tint animate-in fade-in zoom-in-95 duration-200',
        onClick: e => e.stopPropagation(),
      },
      React.createElement(
        'button',
        {
          onClick: onClose,
          className:
            'absolute right-3 top-3 z-20 w-9 h-9 rounded-full bg-[#202124] text-white hover:bg-[#E53935] flex items-center justify-center shadow-md transition-all active:scale-95',
          'aria-label': 'Close modal',
        },
        React.createElement(X, { className: 'w-5 h-5' }),
      ),
      React.createElement(
        'div',
        { className: 'grid grid-cols-1 md:grid-cols-2 max-h-[85vh] overflow-y-auto' },
        React.createElement(
          'div',
          {
            className:
              'p-5 sm:p-6 border-b md:border-b-0 md:border-r border-gray-100 flex flex-col justify-between bg-white',
          },
          React.createElement(
            'div',
            null,
            React.createElement(
              'div',
              { className: 'flex items-start justify-between gap-3 mb-2' },
              React.createElement(
                'div',
                null,
                React.createElement(
                  'h2',
                  {
                    className:
                      'text-xl sm:text-2xl font-heading font-bold text-[#202124] leading-snug',
                  },
                  item.name,
                ),
                React.createElement(
                  'div',
                  { className: 'flex items-center gap-1.5 mt-1 text-panda-red' },
                  React.createElement(
                    'div',
                    { className: 'flex' },
                    [1, 2, 3, 4, 5].map(s =>
                      React.createElement(Star, {
                        key: s,
                        className: 'w-3.5 h-3.5 fill-panda-red text-panda-red',
                      }),
                    ),
                  ),
                  React.createElement(
                    'span',
                    { className: 'text-xs text-gray-500 font-medium' },
                    '(4.9)',
                  ),
                ),
              ),
              React.createElement(
                'div',
                { className: 'flex-shrink-0 flex items-center gap-1' },
                React.createElement(
                  'div',
                  {
                    className:
                      'w-9 h-9 rounded-full overflow-hidden border border-gray-200 bg-panda-tint flex items-center justify-center',
                  },
                  React.createElement('img', {
                    src: restaurant.avatarImage,
                    alt: restaurant.name,
                    className: 'w-full h-full object-cover',
                    referrerPolicy: 'no-referrer',
                    onError: e => {
                      e.target.onerror = null;
                      e.target.src = '/panda_avatar.jpg';
                    },
                  }),
                ),
              ),
            ),
            React.createElement(
              'div',
              {
                className:
                  'inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-[#E53935] font-heading font-bold text-sm mb-4',
              },
              React.createElement(
                'span',
                null,
                '$',
                (options[0]?.price || item.price || 0).toFixed(2),
              ),
              item.servingStyle &&
                React.createElement(
                  'span',
                  { className: 'text-xs text-gray-500 font-normal' },
                  ' / ',
                  item.servingStyle,
                ),
            ),
            React.createElement(
              'div',
              {
                className:
                  'relative rounded-2xl overflow-hidden aspect-[4/3] mb-4 group shadow-sm bg-gray-100',
              },
              React.createElement('img', {
                src: item.image,
                alt: item.name,
                className:
                  'w-full h-full object-cover transition-transform duration-300 group-hover:scale-105',
                referrerPolicy: 'no-referrer',
                onError: e => {
                  e.target.onerror = null;
                  e.target.src = '/meal_default.png';
                },
              }),
              React.createElement(
                'button',
                {
                  onClick: () => setIsFavorite(!isFavorite),
                  className:
                    'absolute top-3 right-3 p-2 rounded-full bg-white/90 backdrop-blur-xs text-gray-600 hover:text-[#E53935] shadow-sm transition-colors',
                },
                React.createElement(Heart, {
                  className: `w-4 h-4 ${isFavorite ? 'fill-[#E53935] text-[#E53935]' : ''}`,
                }),
              ),
              item.calories &&
                React.createElement(
                  'span',
                  {
                    className:
                      'absolute bottom-2 left-2 bg-[#202124]/80 text-white text-[11px] font-medium px-2 py-0.5 rounded-md backdrop-blur-xs',
                  },
                  item.calories,
                  ' kcal',
                ),
            ),
            React.createElement(
              'p',
              { className: 'text-xs sm:text-sm text-gray-600 leading-relaxed' },
              item.fullDescription || item.description,
            ),
          ),
          React.createElement(
            'div',
            {
              className:
                'mt-4 p-2.5 rounded-xl bg-panda-tint border border-panda-soft/60 flex items-start gap-2',
            },
            React.createElement(ShieldAlert, {
              className: 'w-4 h-4 text-[#E53935] flex-shrink-0 mt-0.5',
            }),
            React.createElement(
              'p',
              { className: 'text-[11px] text-gray-600 leading-snug' },
              'Made fresh to order with authentic seasonal ingredients by ',
              restaurant.name,
              '.',
            ),
          ),
        ),
        React.createElement(
          'div',
          { className: 'p-5 sm:p-6 bg-panda-tint/40 flex flex-col justify-between' },
          React.createElement(
            'div',
            { className: 'space-y-5' },
            React.createElement(
              'div',
              null,
              React.createElement(
                'p',
                {
                  className:
                    'text-xs font-bold text-gray-500 uppercase tracking-wider mb-2',
                },
                'Order Selection (Minimum of 1)',
              ),
              React.createElement(
                'div',
                { className: 'space-y-2' },
                options.map(option =>
                  React.createElement(
                    'div',
                    {
                      key: option.id,
                      className:
                        'bg-white rounded-xl p-3.5 border border-gray-200 shadow-xs flex items-center justify-between',
                    },
                    React.createElement(
                      'div',
                      null,
                      React.createElement(
                        'span',
                        {
                          className:
                            'font-heading font-bold text-sm text-[#202124] block',
                        },
                        option.name,
                      ),
                      React.createElement(
                        'span',
                        { className: 'text-xs font-semibold text-[#E53935]' },
                        '$',
                        option.price.toFixed(2),
                      ),
                    ),
                    React.createElement(
                      'div',
                      { className: 'flex items-center gap-3' },
                      React.createElement(
                        'button',
                        {
                          type: 'button',
                          onClick: () =>
                            setOptionQuantity(
                              option.id,
                              (quantities[option.id] || 0) - 1,
                            ),
                          disabled: (quantities[option.id] || 0) <= 0,
                          className:
                            'w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:border-[#E53935] hover:text-[#E53935] disabled:opacity-30 disabled:pointer-events-none transition-colors',
                          'aria-label': `Decrease ${option.name}`,
                        },
                        React.createElement(Minus, { className: 'w-3.5 h-3.5' }),
                      ),
                      React.createElement(
                        'span',
                        {
                          className:
                            'font-bold text-base text-[#202124] min-w-[20px] text-center',
                        },
                        quantities[option.id] || 0,
                      ),
                      React.createElement(
                        'button',
                        {
                          type: 'button',
                          onClick: () =>
                            setOptionQuantity(
                              option.id,
                              (quantities[option.id] || 0) + 1,
                            ),
                          className:
                            'w-8 h-8 rounded-full border border-[#E53935] bg-[#E53935] text-white flex items-center justify-center hover:bg-[#d32f2f] transition-colors',
                          'aria-label': `Increase ${option.name}`,
                        },
                        React.createElement(Plus, { className: 'w-3.5 h-3.5' }),
                      ),
                    ),
                  ),
                ),
              ),
              React.createElement(
                'div',
                { className: 'mt-2 text-right' },
                !showRecipientInput
                  ? React.createElement(
                      'button',
                      {
                        type: 'button',
                        onClick: () => setShowRecipientInput(true),
                        className:
                          'text-xs font-semibold text-[#E53935] hover:underline inline-flex items-center gap-1',
                      },
                      '+ Add Name for Meal Label',
                    )
                  : React.createElement(
                      'div',
                      { className: 'mt-1 text-left' },
                      React.createElement(
                        'label',
                        { className: 'text-[11px] text-gray-500 font-medium' },
                        "Meal Label (e.g. Kim's Lunch)",
                      ),
                      React.createElement('input', {
                        type: 'text',
                        placeholder: 'Who is this portion for?',
                        value: recipientName,
                        onChange: e => setRecipientName(e.target.value),
                        className:
                          'w-full mt-0.5 px-3 py-1.5 text-xs bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#E53935]',
                      }),
                    ),
              ),
            ),
            React.createElement(
              'div',
              null,
              React.createElement(
                'label',
                {
                  className:
                    'text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1.5',
                },
                'Special Instructions',
              ),
              React.createElement('textarea', {
                rows: 3,
                placeholder:
                  'Please type in any special instructions or allergy notes you have...',
                value: instructions,
                onChange: e => setInstructions(e.target.value),
                className:
                  'w-full p-3 bg-white rounded-xl border border-gray-200 text-xs text-[#202124] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E53935] focus:border-transparent resize-none shadow-xs',
              }),
            ),
            React.createElement(
              'div',
              { className: 'pt-2 border-t border-gray-200 space-y-1' },
              React.createElement(
                'div',
                { className: 'flex justify-between text-xs text-gray-600' },
                React.createElement('span', null, 'Total Portions:'),
                React.createElement(
                  'span',
                  { className: 'font-bold text-[#202124]' },
                  totalPortions,
                ),
              ),
              React.createElement(
                'div',
                { className: 'flex justify-between text-sm font-bold text-[#202124]' },
                React.createElement('span', null, 'Total Price:'),
                React.createElement(
                  'span',
                  { className: 'text-[#E53935] text-base' },
                  '$',
                  totalPrice.toFixed(2),
                ),
              ),
            ),
          ),
          React.createElement(
            'div',
            { className: 'mt-5' },
            React.createElement(
              'button',
              {
                id: 'modal-add-to-cart-btn',
                type: 'button',
                onClick: handleSave,
                disabled: totalPortions <= 0,
                className:
                  'w-full py-3.5 px-6 rounded-xl bg-[#E53935] hover:bg-[#d32f2f] text-white font-heading font-bold text-base shadow-md shadow-red-500/20 active:scale-98 transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none',
              },
              React.createElement('span', null, 'Save Changes'),
              React.createElement('span', null, '•'),
              React.createElement('span', null, '$', totalPrice.toFixed(2)),
            ),
          ),
        ),
      ),
    ),
  );
};
